/**
 * Matériel roulant et constantes physiques.
 * Port fidèle de tools/model_reference.py — toute valeur ici doit y être identique.
 */
export const G = 9.81;
export const ETA = 0.85;
export const JERK = 0.6;

/** Davis pour ~110 t chargées : A (N), B (N·s/m), C (N·s²/m²). Mis à l'échelle sur la masse. */
export const DAVIS: readonly [number, number, number] = [1500.0, 30.0, 4.0];
export const DAVIS_REF_MASS = 110e3;

export type StockId = "flirt" | "coradia" | "civity" | "dualis" | "citylink" | "oracle";

export interface RollingStock {
  id: StockId;
  label: string;
  /** masse en charge, kg */
  mass_kg: number;
  /** puissance à la jante, W */
  power_w: number;
  /** accélération de démarrage, m/s² */
  a0: number;
  /** décélération de service, m/s² */
  d: number;
  /** vitesse maximale homologuée, m/s */
  vmax_ms: number;
  family: "régional" | "tram-train" | "oracle";
  note?: string;
}

export const STOCK: Record<StockId, RollingStock> = {
  flirt: {
    id: "flirt", label: "Stadler FLIRT", mass_kg: 110e3, power_w: 2.6e6, a0: 1.1, d: 1.1,
    vmax_ms: 110 / 3.6, family: "régional", note: "automotrice régionale légère, défaut",
  },
  coradia: {
    id: "coradia", label: "Alstom Coradia", mass_kg: 120e3, power_w: 2.2e6, a0: 0.95, d: 1.0,
    vmax_ms: 110 / 3.6, family: "régional", note: "Régiolis / Continental",
  },
  civity: {
    id: "civity", label: "CAF Civity", mass_kg: 115e3, power_w: 2.4e6, a0: 1.0, d: 1.0,
    vmax_ms: 110 / 3.6, family: "régional",
  },
  dualis: {
    id: "dualis", label: "Citadis Dualis", mass_kg: 75e3, power_w: 0.9e6, a0: 1.05, d: 1.2,
    vmax_ms: 100 / 3.6, family: "tram-train", note: "tram-train, homologué 100 km/h",
  },
  citylink: {
    id: "citylink", label: "Stadler Citylink", mass_kg: 66e3, power_w: 0.9e6, a0: 1.1, d: 1.2,
    vmax_ms: 100 / 3.6, family: "tram-train", note: "tram-train, homologué 100 km/h",
  },
  // matériel fictif calé sur les défauts de la forme close : v1 = η·P/(m·a0) = 11 m/s
  oracle: {
    id: "oracle", label: "Oracle forme close", mass_kg: 110e3, power_w: (11 * 110e3) / ETA,
    a0: 1.0, d: 1.0, vmax_ms: 30.6, family: "oracle",
  },
};

/** Presets proposés dans l'interface (l'oracle ne sert qu'aux tests). */
export const STOCK_CHOICES: StockId[] = ["flirt", "coradia", "civity", "dualis", "citylink"];
export const DEFAULT_STOCK: StockId = "flirt";

/** Vitesse de base v1 = η·P/(m·a0) : fin de la zone à effort constant. */
export function baseSpeed(s: RollingStock): number {
  return (ETA * s.power_w) / (s.mass_kg * s.a0);
}

/** Coefficients de Davis mis à l'échelle sur la masse du matériel. */
export function davisFor(s: RollingStock): [number, number, number] {
  const k = s.mass_kg / DAVIS_REF_MASS;
  return [DAVIS[0] * k, DAVIS[1] * k, DAVIS[2] * k];
}

/** Pénalité de jerk à chaque arrêt : (a0 + d)/j, en secondes. */
export function jerkPenalty(s: RollingStock): number {
  return (s.a0 + s.d) / JERK;
}
